import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { api_url } from "../config/config";

const ThankYou = () => {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { order_id, order_number, lookup_order_id } = location.state || {};
  // Same field (orderId) and same /order/:id endpoint Order Details /
  // My Orders use, so this always matches instead of showing an unrelated
  // fabricated number.
  const [orderNum, setOrderNum] = useState(order_number || order_id || null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setMounted(true), 100);
  }, []);

  useEffect(() => {
    const lookupId = lookup_order_id || order_id;
    if (!lookupId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${api_url}/order/${lookupId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });
        setOrderNum((current) =>
          data?.data?.orderId ||
          data?.data?.details?.[0]?.orderId ||
          current,
        );
      } catch (e) {
        console.error("Error fetching order id:", e);
      }
    })();
  }, [lookup_order_id, order_id]);

  const steps = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
      label: "Order Confirmed",
      done: true,
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
      ),
      label: "Processing",
      done: false,
      active: true,
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      ),
      label: "Out for Delivery",
      done: false,
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      ),
      label: "Delivered",
      done: false,
    },
  ];

  return (
    <div
      className="min-h-screen mt-36 lg:mt-0 bg-[#f8fafa] font-poppins flex flex-col items-center justify-center px-4 py-12 sm:py-16 relative overflow-hidden"
   
    >
      <Helmet>
        <title>Order Confirmed | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="Your MediSYS Pharmacy order has been placed successfully. View your order details and delivery status."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Background decorative blobs */}
      <div className="absolute top-0 right-0 w-[350px] h-[350px] rounded-full bg-[#eaf4f3] opacity-60 translate-x-1/3 -translate-y-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-[#fff3ee] opacity-60 -translate-x-1/3 translate-y-1/3 pointer-events-none" />

      {/* Main card */}
      <div
        className="relative bg-white rounded-3xl border-[1.5px] border-[#e2eaea] shadow-[0_20px_60px_rgba(22,44,42,0.12)] w-full max-w-[520px] overflow-hidden transition-all duration-700"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(24px)",
        }}
      >
        {/* Rainbow accent top */}
        <div className="h-[4px] w-full bg-gradient-to-r from-[#1e4a46] via-[#5fbdb3] to-[#ff601e]" />

        {/* Lottie + confetti area */}
        <div className="relative bg-gradient-to-b from-[#eaf4f3] to-white pt-6 pb-2 flex flex-col items-center">
          {/* Floating pill badges */}
          <div className="absolute top-5 left-5 flex items-center gap-1.5 bg-white border border-[#d4ecea] rounded-full px-3 py-1.5 shadow-sm">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span className="text-[11px] font-bold text-[#1e4a46]">Order Placed</span>
          </div>
          <div className="absolute top-5 right-5 flex items-center gap-1.5 bg-[#fff3ee] border border-[#ffd5c2] rounded-full px-3 py-1.5 shadow-sm">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff601e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span className="text-[11px] font-bold text-[#ff601e]">Free Delivery</span>
          </div>

          <div className="w-[220px] h-[180px] sm:w-[260px] sm:h-[210px]">
            <DotLottieReact
              src="https://lottie.host/334b83c2-c3d5-467a-87fa-92e8d3018908/dV91hDjTed.lottie"
              loop
              autoplay
            />
          </div>
        </div>

        {/* Text content */}
        <div className="px-7 sm:px-9 pb-7 pt-4 flex flex-col items-center gap-5">

          {/* Heading */}
          <div className="text-center">
            <h1 className="text-[26px] sm:text-[30px] font-black text-[#0a1715] leading-tight mb-1.5" style={{ fontFamily: "'Nunito',sans-serif" }}>
              Order Placed! 🎉
            </h1>
            <p className="text-[14px] text-[#5a7a77] font-medium leading-relaxed">
              Your medicines are on their way. Sit back and relax — we've got you covered.
            </p>
          </div>

          {/* Order number pill */}
          <div className="flex items-center gap-3 bg-[#f4fbfa] border border-[#d4ecea] rounded-2xl px-5 py-3 w-full">
            <div className="w-10 h-10 rounded-xl bg-[#1e4a46] flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-[#5a7a77] uppercase tracking-wider mb-0.5">Order ID</p>
              <p className="text-[15px] font-black text-[#1e4a46]" style={{ fontFamily: "'Nunito',sans-serif" }}>
                {orderNum || "See My Orders for details"}
              </p>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </div>

          {/* Order progress tracker */}
          <div className="w-full">
            <p className="text-[11px] font-bold text-[#5a7a77] uppercase tracking-wider mb-3">Delivery Status</p>
            <div className="flex items-center justify-between relative">
              {/* Connector line */}
              <div className="absolute top-5 left-5 right-5 h-[2px] bg-[#e2eaea] z-0" />
              <div className="absolute top-5 left-5 h-[2px] bg-[#1e4a46] z-0 transition-all duration-1000"
                style={{ width: mounted ? "33%" : "0%" }} />

              {steps.map((step, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 z-10 relative flex-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 border-[1.5px]
                    ${step.done
                      ? "bg-[#1e4a46] border-[#1e4a46] text-white shadow-[0_4px_12px_rgba(22,44,42,0.25)]"
                      : step.active
                        ? "bg-[#ff601e] border-[#ff601e] text-white shadow-[0_4px_12px_rgba(255,96,30,0.3)]"
                        : "bg-white border-[#e2eaea] text-[#c8d8d7]"}`}>
                    {step.icon}
                  </div>
                  <span className={`text-[9px] font-bold uppercase tracking-wide text-center leading-tight
                    ${step.done ? "text-[#1e4a46]" : step.active ? "text-[#ff601e]" : "text-[#c8d8d7]"}`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info strip */}
          <div className="flex items-center gap-2.5 bg-[#fff3ee] border border-[#ffd5c2] rounded-2xl px-4 py-3 w-full">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff601e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-[12px] text-[#c44a14] font-medium leading-snug">
              You'll receive an SMS &amp; WhatsApp update once your order ships.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Link to="/home" className="flex-1" style={{ textDecoration: "none" }}>
              <button className="w-full flex items-center justify-center gap-2.5 bg-[#1e4a46] hover:bg-[#ff601e] text-white font-bold text-[14px] h-12 rounded-xl transition-all duration-200 border-none cursor-pointer shadow-[0_4px_16px_rgba(22,44,42,0.25)] hover:shadow-[0_6px_22px_rgba(255,96,30,0.4)] hover:-translate-y-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                Continue Shopping
              </button>
            </Link>
            <Link to="/my-orders" className="flex-1" style={{ textDecoration: "none" }}>
              <button className="w-full flex items-center justify-center gap-2.5 bg-white hover:bg-[#f4fbfa] text-[#1e4a46] font-bold text-[14px] h-12 rounded-xl transition-all duration-200 border-[1.5px] border-[#d4ecea] cursor-pointer hover:border-[#5fbdb3]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                My Orders
              </button>
            </Link>
          </div>

          {/* Footer note */}
          <p className="text-[11px] text-[#aac9c7] text-center leading-relaxed">
            Need help? Call us at{" "}
            <a href="tel:+917799923501" className="text-[#5fbdb3] font-semibold" style={{ textDecoration:"none" }}>
              +91 7799923501
            </a>
            {" "}or{" "}
            <Link to="/contact-us" className="text-[#5fbdb3] font-semibold" style={{ textDecoration:"none" }}>
              chat with us
            </Link>
          </p>
        </div>
      </div>

      {/* Bottom tagline */}
      <p
        className="mt-6 text-[12px] text-[#aac9c7] font-medium text-center transition-all duration-1000"
        style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(8px)" }}
      >
        MediSYS Pharmacy — Where Healthcare Meets Convenience ✦
      </p>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@800;900&family=Poppins:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
};

export default ThankYou;
